import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/Button';
import { ArrowRight, Play, FileText, CheckCircle2, X } from 'lucide-react';
export function Hero() {
  const [showVideo, setShowVideo] = useState(false);
  return <section className="relative pt-20 pb-16 md:pt-32 md:pb-24 overflow-hidden bg-white" data-id="element-76">
      {/* Navigation */}
      <nav className="absolute top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-sm border-b border-slate-200" data-id="element-77">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-78">
          <div className="flex items-center justify-between h-16" data-id="element-79">
            <div className="flex items-center gap-8" data-id="element-80">
              <a href="/" className="flex flex-col" data-id="element-81">
                <div className="flex items-center gap-2" data-id="element-82">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/46nVjihkQgtRGHjwuLjy14/Untitled_design_%282%29.png" alt="Foundations" className="h-8 w-8" data-id="element-83" />
                  <span className="text-xl font-bold text-slate-900" data-id="element-84">
                    Foundations
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium ml-10" data-id="element-85">
                  <span data-id="element-86">Powered by</span>
                  <a href="https://www.thruline.ai" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity" data-id="element-87">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/bxVHxBAzSWANMpXq8oviFD/thruline-logo-light-trans_%281%29.png" alt="Thruline" className="h-3 w-auto" data-id="element-88" />
                  </a>
                </div>
              </a>
              <a href="/how-it-works" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" data-id="element-89">
                How It Works
              </a>
            </div>
            <Button size="sm" onClick={() => window.open('https://calendly.com/alex-thruline/erp-client-portal', '_blank')} data-id="element-90">
              Schedule Demo
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-91">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-92">
          {/* Left Column: Copy */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="flex flex-col items-start max-w-2xl" data-id="element-93">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.1] mb-6" data-id="element-94">
              Track every system, requirement, and document for your ERP
              clients.
            </h2>
            <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed max-w-lg" data-id="element-95">
              Paste a transcript and get project requirements, project plans and
              complete data imports in minutes. Never lose track of client
              systems or implementation details again.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto mb-6" data-id="element-96">
              <Button size="lg" rightIcon={<ArrowRight className="h-4 w-4" data-id="element-98" />} onClick={() => window.open('https://calendly.com/alex-thruline/erp-client-portal', '_blank')} data-id="element-97">
                Schedule Demo
              </Button>
              <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} onClick={() => setShowVideo(true)} className="inline-flex items-center justify-center h-12 px-6 text-base font-medium rounded-lg border-2 border-slate-300 bg-white text-slate-700 hover:border-transparent hover:bg-gradient-primary hover:text-white transition-all duration-300 shadow-sm" data-id="element-99">
                <Play className="h-4 w-4 mr-2" data-id="element-100" />
                Watch Demo
              </motion.button>
            </div>

            <p className="text-sm text-slate-500 font-medium" data-id="element-101">
              Built for boutique ERP consultancies
            </p>
          </motion.div>

          {/* Right Column: Visual Mockup */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="relative" data-id="element-102">
            <div className="relative rounded-xl bg-slate-900 p-2 shadow-2xl ring-1 ring-slate-900/10" data-id="element-103">
              <div className="rounded-lg bg-slate-50 overflow-hidden aspect-[4/3] md:aspect-[16/10] flex" data-id="element-104">
                {/* Left Panel: Transcript (Blurred) */}
                <div className="w-1/3 border-r border-slate-200 bg-white p-4 flex flex-col gap-3" data-id="element-105">
                  <div className="flex items-center gap-2 mb-2 border-b border-slate-100 pb-2" data-id="element-106">
                    <div className="h-2 w-2 rounded-full bg-red-400" data-id="element-107"></div>
                    <div className="h-2 w-2 rounded-full bg-yellow-400" data-id="element-108"></div>
                    <div className="h-2 w-2 rounded-full bg-green-400" data-id="element-109"></div>
                    <span className="text-[10px] text-slate-400 ml-1" data-id="element-110">
                      transcript.txt
                    </span>
                  </div>
                  {[...Array(12)].map((_, i) => <div key={i} className="flex gap-2" data-id="element-111">
                      <div className="h-2 w-6 bg-slate-100 rounded-full shrink-0" data-id="element-112"></div>
                      <div className={`h-2 rounded-full bg-slate-100 w-full ${i % 2 === 0 ? 'w-3/4' : 'w-5/6'}`} data-id="element-113"></div>
                    </div>)}
                  <div className="mt-auto p-3 bg-gradient-to-r from-primary-purple/10 to-accent/10 rounded border border-primary-cyan/20" data-id="element-114">
                    <div className="flex items-center gap-2 text-primary-blue text-[10px] font-medium" data-id="element-115">
                      <div className="animate-pulse h-1.5 w-1.5 rounded-full bg-primary-blue" data-id="element-116"></div>
                      Processing...
                    </div>
                  </div>
                </div>

                {/* Right Panel: Generated SOW */}
                <div className="w-2/3 bg-white p-6 flex flex-col" data-id="element-117">
                  <div className="flex justify-between items-center mb-6" data-id="element-118">
                    <div className="flex items-center gap-2" data-id="element-119">
                      <div className="p-1.5 bg-gradient-to-br from-primary-purple/10 to-accent/10 rounded text-primary-blue" data-id="element-120">
                        <FileText className="h-4 w-4" data-id="element-121" />
                      </div>
                      <div data-id="element-122">
                        <div className="text-xs font-semibold text-slate-900" data-id="element-123">
                          Generated SOW
                        </div>
                        <div className="text-[10px] text-slate-500" data-id="element-124">
                          Project Alpha • v1.0
                        </div>
                      </div>
                    </div>
                    <div className="px-2 py-1 bg-green-50 text-green-700 text-[10px] font-medium rounded-full border border-green-100 flex items-center gap-1" data-id="element-125">
                      <CheckCircle2 className="h-3 w-3" data-id="element-126" /> Ready
                    </div>
                  </div>

                  {/* SOW Table Mock */}
                  <div className="border border-slate-200 rounded-lg overflow-hidden flex-1" data-id="element-127">
                    <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex gap-4" data-id="element-128">
                      <div className="w-1/2 text-[10px] font-medium text-slate-500" data-id="element-129">
                        Scope Item
                      </div>
                      <div className="w-1/4 text-[10px] font-medium text-slate-500" data-id="element-130">
                        Phase
                      </div>
                      <div className="w-1/4 text-[10px] font-medium text-slate-500" data-id="element-131">
                        Est. Hours
                      </div>
                    </div>
                    {[{
                    item: 'Data Migration Strategy',
                    phase: 'Discovery',
                    hours: '24h'
                  }, {
                    item: 'Finance Module Config',
                    phase: 'Build',
                    hours: '40h'
                  }, {
                    item: 'User Acceptance Testing',
                    phase: 'Validation',
                    hours: '16h'
                  }, {
                    item: 'Go-Live Support',
                    phase: 'Deploy',
                    hours: '32h'
                  }].map((row, i) => <div key={i} className="px-4 py-3 border-b border-slate-100 flex gap-4 items-center last:border-0" data-id="element-132">
                        <div className="w-1/2 text-[11px] font-medium text-slate-700" data-id="element-133">
                          {row.item}
                        </div>
                        <div className="w-1/4" data-id="element-134">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-slate-100 text-slate-600" data-id="element-135">
                            {row.phase}
                          </span>
                        </div>
                        <div className="w-1/4 text-[11px] text-slate-500 font-mono" data-id="element-136">
                          {row.hours}
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Video Modal */}
      <AnimatePresence data-id="element-137">
        {showVideo && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={() => setShowVideo(false)} data-id="element-138">
            <motion.div initial={{
          scale: 0.9
        }} animate={{
          scale: 1
        }} exit={{
          scale: 0.9
        }} className="bg-white rounded-2xl p-6 max-w-5xl w-full shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-139">
              <div className="flex items-center justify-between mb-4" data-id="element-140">
                <h3 className="text-2xl font-bold text-slate-900" data-id="element-141">
                  Product Demo
                </h3>
                <button onClick={() => setShowVideo(false)} className="text-slate-600 hover:text-slate-900 transition-colors p-2 hover:bg-slate-100 rounded-lg" data-id="element-142">
                  <X className="h-6 w-6" data-id="element-143" />
                </button>
              </div>
              <div className="aspect-video rounded-lg overflow-hidden bg-slate-100" data-id="element-144">
                <iframe src="https://www.loom.com/embed/224e54ecaf844fcc9e98db1fc3a2236a?sid=8f3e4c5d-9b0a-4f3e-8e5d-9c8f7e6d5c4b" frameBorder="0" allowFullScreen className="w-full h-full" data-id="element-145"></iframe>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}